import React from "react";
import { useConsentUi } from "./ConsentProvider";
import { useTranslation } from "@/lib/i18n";

/**
 * Consent Foundation — permanent bilingual footer control.
 *
 * Rules (spec section 6):
 *   * Available from every page that mounts the shared Footer.
 *   * Opens the settings dialog without a page reload.
 *   * Remains available after every decision type.
 *   * It is a real button (not a misleading nav link) because the
 *     action is an in-page settings action, not a route change.
 *
 * WS-4 STAGING FIX (2026-08-06) — visibility hardening.  The default
 * classes now render the control as a persistent underlined link-
 * button that is visually identifiable as a control even against the
 * dim `text-muted-foreground/55` copyright row.  We deliberately
 * avoid a card/button chrome so the footer aesthetic is preserved —
 * this is a link-styled affordance, not a primary CTA.  Callers can
 * still fully override via the `className` prop when a different
 * surface is needed.
 */
export default function CookieSettingsButton({ className }) {
  const { openSettings } = useConsentUi();
  const { t } = useTranslation();
  const label = t("consent.footer_settings_button");
  return (
    <button
      type="button"
      onClick={openSettings}
      aria-label={label}
      className={
        className ||
        "text-[11.5px] tracking-wide font-medium text-muted-foreground/90 hover:text-foreground underline underline-offset-4 decoration-muted-foreground/50 hover:decoration-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-sm"
      }
      data-testid="footer-cookie-settings-button"
    >
      {label}
    </button>
  );
}
